import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, WOOD, cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { gear } from './clockwork.ts';
import {
  ASHLAR, IRON_BAND, IRON_PLATE, IRON_RIVET, IRON_STONE, crack, crackRound, gable, plate, roundClad, rubbleAround, snapTop, splinters, stain,
  type Level, type Wear,
} from './structures.ts';
import { LIGHT_K, begin, done, part, pointLight, type BaseOpts, type Model } from './types.ts';

/*
 * Era II: the artificer's workshop and the clockwork turrets it makes, the alchemist's laboratory, and the
 * iron mines out on the shell. The artificer's look carries over from the clockwork allies: dark iron,
 * cold steel plate, copper-red trim and a cyan core (no brass, no gold). The laboratory glows with what it
 * brews: acid green-yellow, frost blue, fire orange and a violet tincture, under a verdigris dome.
 */

/** Building material: 1 timber and fieldstone, 2 dressed stone, 3 stone clad in iron. */
export type Era2Material = 1 | 2 | 3;

export interface Era2Opts extends BaseOpts {
  material?: Era2Material;
  wear?: Wear;
}

export interface ClockworkOpts extends Era2Opts {
  level?: Level;
}

// No environment map on the table: plate keeps some diffuse so it reads as painted steel, not a black hole.
const PLATE = std('#808a96', { metalness: 0.5, roughness: 0.38, flatShading: true });
const PLATE_HI = metal('#b9c1cb', { roughness: 0.25, flatShading: true });
/** Dark iron: the same material as the cladding's bands, so they batch together. */
const IRON = IRON_BAND;
const COPPER = metal('#a4512f', { roughness: 0.4 });

/** Blued spring steel. */
const BLUED = metal('#4b5d80', { roughness: 0.3 });
const CORE = emis('#d9fbff', '#3fd4ff', 1.8);
const CYAN = '#4fdcff';
const VERDIGRIS = std('#4f8f86', { roughness: 0.6, metalness: 0.35, flatShading: true });
const PLINTH = stone('#5f5b55');
const FORGE = emis('#ffb070', '#ff5a10', 1.4);
const GLASS = std('#cfe6ee', { roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.35, depthWrite: false, bumpScale: 0 });

/** Brews of the laboratory. Acid is a sickly green-yellow: yellower than the cursed-tower and wisp greens. */
export const BREW = { acid: '#c6e21a', frost: '#7fd0ff', fire: '#ff8a2a', violet: '#b06bff' } as const;
const brew = (c: string, b = 1.1): THREE.MeshStandardMaterial => emis(c, c, b, { roughness: 0.2, bumpScale: 0 });

const box = (parent: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, parent);
  m.position.set(x, y, z);
  m.rotation.y = ry;
  return m;
};

const rivet = (parent: THREE.Object3D, x: number, y: number, z: number, r = 0.012): void => {
  M(new THREE.OctahedronGeometry(r, 0), IRON_RIVET, parent).position.set(x, y, z);
};

/**
 * A pitched roof of two slabs over (cx, y0, cz): ridge along X (or Z), `span` across, `h` high at the ridge,
 * `len` long, eaves hanging `over` past the walls. The gable ends are closed by a prism of `endMat`.
 * Returns the slope angle and the roof group (local frame: ridge along X).
 */
export const pitchedRoof = (
  parent: THREE.Object3D, cx: number, y0: number, cz: number, span: number, h: number, len: number, mat: THREE.Material, endMat: THREE.Material,
  alongZ = false, over = 0.06, thick = 0.035,
): { angle: number; grp: THREE.Group; slab: number } => {
  const grp = G(parent, cx, y0, cz);
  if (alongZ) grp.rotation.y = Math.PI / 2;
  const half = span / 2;
  const angle = Math.atan2(h, half);
  const L = Math.hypot(half, h) + over;
  for (const side of [1, -1]) {
    const slab = box(grp, mat, len + over * 1.4, thick, L, 0, 0, 0);
    slab.rotation.x = side * angle;
    // Mid-slope, pushed down the slope by half the overhang and up off the wall by half the slab.
    slab.position.set(0, h / 2 - (Math.sin(angle) * over) / 2 + (Math.cos(angle) * thick) / 2, side * (half / 2 + (Math.cos(angle) * over) / 2 + (Math.sin(angle) * thick) / 2));
  }
  box(grp, mat, len + over * 1.6, 0.04, 0.07, 0, h + 0.02, 0);
  gable(span, h, len, endMat, grp);
  return { angle, grp, slab: L };
};

/** Standing seams down both slopes of an iron roof (in the roof group's frame). */
export const seams = (grp: THREE.Group, span: number, h: number, len: number, angle: number, step = 0.12): void => {
  const half = span / 2;
  const L = Math.hypot(half, h) + 0.06;
  for (const side of [1, -1]) {
    for (let x = -len / 2 + step / 2; x < len / 2; x += step) {
      const s = box(grp, IRON_BAND, 0.016, 0.022, L, x, 0, 0);
      s.rotation.x = side * angle;
      s.position.set(x, h / 2 + 0.03, side * (half / 2 + 0.02));
    }
  }
};

/** Iron straps and angle irons on a box body (center cx, cz; w × d; from y0 to y1), with plates on chosen faces. */
export const boxClad = (
  parent: THREE.Object3D, cx: number, cz: number, w: number, d: number, y0: number, y1: number, straps: number[], skirt: number, faces: ('f' | 'b' | 'l' | 'r')[],
): void => {
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(parent, IRON_BAND, 0.035, y1 - y0, 0.035, cx + (sx * w) / 2, (y0 + y1) / 2, cz + (sz * d) / 2);
  for (const y of straps) {
    box(parent, IRON_BAND, w + 0.02, 0.035, 0.012, cx, y, cz + d / 2 + 0.004);
    box(parent, IRON_BAND, w + 0.02, 0.035, 0.012, cx, y, cz - d / 2 - 0.004);
    box(parent, IRON_BAND, 0.012, 0.035, d + 0.02, cx + w / 2 + 0.004, y, cz);
    box(parent, IRON_BAND, 0.012, 0.035, d + 0.02, cx - w / 2 - 0.004, y, cz);
  }
  if (skirt <= y0) return;
  const h = skirt - y0 - 0.02;
  const yc = y0 + 0.01 + h / 2;
  const row = (len: number, place: (x: number, pw: number) => void): void => {
    const n = Math.max(1, Math.round(len / 0.3));
    const pw = len / n - 0.012;
    for (let i = 0; i < n; i++) place(-len / 2 + (i + 0.5) * (len / n), pw);
  };
  if (faces.includes('f')) row(w - 0.04, (x, pw) => plate(parent, cx + x, yc, cz + d / 2 + 0.009, pw, h, 0));
  if (faces.includes('b')) row(w - 0.04, (x, pw) => plate(parent, cx + x, yc, cz - d / 2 - 0.009, pw, h, Math.PI));
  if (faces.includes('r')) row(d - 0.04, (z, pw) => plate(parent, cx + w / 2 + 0.009, yc, cz - z, pw, h, Math.PI / 2));
  if (faces.includes('l')) row(d - 0.04, (z, pw) => plate(parent, cx - w / 2 - 0.009, yc, cz + z, pw, h, -Math.PI / 2));
};

/** Quoins: dressed corner stones, alternating long and short, up a box corner. */
const quoins = (parent: THREE.Object3D, mat: THREE.Material, cx: number, cz: number, w: number, d: number, y0: number, y1: number): void => {
  const n = Math.floor((y1 - y0) / 0.085);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    for (let i = 0; i < n; i++) {
      const long = (i + (sx * sz > 0 ? 0 : 1)) % 2 === 0;
      const y = y0 + 0.04 + i * 0.085;
      box(parent, mat, long ? 0.13 : 0.07, 0.07, long ? 0.07 : 0.13, cx + sx * (w / 2 - (long ? 0.055 : 0.025)), y, cz + sz * (d / 2 - (long ? 0.025 : 0.055))).position.x += sx * 0.012;
    }
  }
};

/* ---------- the artificer's workshop ---------- */

/**
 * A sturdy two-storey workshop: a stone ground floor (timber-framed upper storey in wood, all stone in
 * stone, plated and riveted in iron) under a pitched roof with an iron gear on its gable. The forge glows
 * through the open double doors; a great flywheel turns beside the wall (part 'spin'), a timber crane
 * swings a gear on its chain, an unfinished automaton waits on its trestle and the bench is strewn with
 * work. Worn (1): cracks, soot and stones at the foot. Crumbling (2): the crane's jib snapped and hanging,
 * the chimney broken, holes in the roof.
 */
export const workshop = (o?: Era2Opts): Model => {
  const mat = o?.material ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.8, 81);
  const { g } = c;
  const wr = mulberry32(810 + mat * 3 + wear);
  const iron = mat === 3;
  const wallM = mat === 1 ? stone('#77736c') : stone(iron ? IRON_STONE : '#8b857a');
  const upperM = mat === 1 ? std('#8e8c87', { roughness: 1, bumpScale: 0.02 }) : wallM;
  const roofM = mat === 1 ? std('#4a2f22', { roughness: 0.95, flatShading: true }) : iron ? metal('#3d434b', { roughness: 0.42, metalness: 0.6, flatShading: true }) : stone('#3e3a36');
  const beam = iron ? IRON_BAND : DWOOD;
  const X = -0.08, Z = -0.1, W = 0.86, D = 0.62;
  const front = Z + D / 2;
  const right = X + W / 2;
  const yLo = 0.08, hLo = 0.34, yUp = yLo + hLo, hUp = 0.27;
  box(g, PLINTH, 1.22, 0.08, 1.02, 0, 0.04, 0);
  // Ground floor.
  box(g, wallM, W, hLo, D, X, yLo + hLo / 2, Z);
  if (mat === 1) {
    // Fieldstone: a few proud stones on the faces the table sees.
    const fs = [PLINTH, stone('#837e75')];
    for (let i = 0; i < 14; i++) {
      const onFront = i < 8;
      const s = 0.035 + wr() * 0.03;
      const m = M(new THREE.DodecahedronGeometry(s, 0), fs[i % 2] as THREE.Material, g);
      m.scale.z = 0.45;
      if (onFront) m.position.set(X - W / 2 + 0.05 + wr() * (W - 0.1), yLo + 0.04 + wr() * (hLo - 0.08), front + 0.005);
      else {
        m.position.set(right + 0.005, yLo + 0.04 + wr() * (hLo - 0.08), Z - D / 2 + 0.05 + wr() * (D - 0.1));
        m.rotation.y = Math.PI / 2;
      }
    }
  } else quoins(g, stone(iron ? '#868a90' : '#a39d92'), X, Z, W, D, yLo, yUp);
  // Upper storey.
  box(g, upperM, W - (mat === 1 ? 0.02 : 0), hUp, D - (mat === 1 ? 0.02 : 0), X, yUp + hUp / 2, Z);
  if (mat === 1) {
    // Timber frame: plates, corner posts, studs and braces on every face.
    box(g, DWOOD, W + 0.02, 0.04, D + 0.02, X, yUp + 0.01, Z);
    box(g, DWOOD, W + 0.02, 0.035, D + 0.02, X, yUp + hUp - 0.025, Z);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, DWOOD, 0.04, hUp, 0.04, X + (sx * W) / 2, yUp + hUp / 2, Z + (sz * D) / 2);
    for (const sz of [-1, 1]) {
      for (const x of [-0.22, 0, 0.22]) box(g, DWOOD, 0.03, hUp, 0.02, X + x, yUp + hUp / 2, Z + sz * (D / 2 - 0.002));
      for (const [x, k] of [[-0.32, 1], [0.32, -1]] as const) {
        const br = box(g, DWOOD, 0.025, hUp * 1.1, 0.02, X + x, yUp + hUp / 2, Z + sz * (D / 2 - 0.002));
        br.rotation.z = k * 0.6;
      }
    }
    for (const sx of [-1, 1]) {
      box(g, DWOOD, 0.02, hUp, 0.03, X + sx * (W / 2 - 0.002), yUp + hUp / 2, Z);
      const br = box(g, DWOOD, 0.02, hUp * 1.1, 0.025, X + sx * (W / 2 - 0.002), yUp + hUp / 2, Z - 0.15);
      br.rotation.x = 0.6;
    }
  } else {
    box(g, stone(iron ? '#5d6167' : '#6f6a63'), W + 0.03, 0.03, D + 0.03, X, yUp, Z);
    quoins(g, stone(iron ? '#868a90' : '#a39d92'), X, Z, W, D, yUp + 0.02, yUp + hUp);
  }
  // Windows of the upper storey: shutters open, a warm light inside.
  const win = emis('#ff9a50', '#ff6a1a', 0.5);
  for (const x of [-0.2, 0.2]) {
    box(g, BLACK, 0.11, 0.1, 0.012, X + x, yUp + 0.14, front + 0.003);
    box(g, win, 0.08, 0.04, 0.012, X + x, yUp + 0.115, front + 0.005);
    for (const k of [-1, 1]) box(g, beam, 0.05, 0.11, 0.012, X + x + k * 0.085, yUp + 0.14, front + 0.012).rotation.y = k * 0.5;
  }
  box(g, BLACK, 0.012, 0.1, 0.11, right + 0.003, yUp + 0.14, Z + 0.02);
  box(g, win, 0.012, 0.04, 0.08, right + 0.005, yUp + 0.115, Z + 0.02);
  // Roof (crumbling: a few boards gone, dark gaps).
  const rh = 0.3;
  const roof = pitchedRoof(g, X, yUp + hUp, Z, D + 0.02, rh, W + 0.02, roofM, upperM);
  if (iron) seams(roof.grp, D + 0.02, rh, W + 0.08, roof.angle);
  else if (mat === 1) {
    // Shingle courses.
    for (const side of [1, -1]) for (let i = 1; i < 4; i++) {
      const k = i / 4;
      const s = box(roof.grp, std('#3a2419', { roughness: 1, flatShading: true }), W + 0.1, 0.012, 0.016, 0, rh * (1 - k) + 0.035, side * ((D + 0.02) / 2) * k + side * 0.012);
      s.rotation.x = side * roof.angle;
    }
  }
  if (wear > 0) {
    for (let i = 0; i < wear * 2; i++) {
      const side = i % 2 ? -1 : 1;
      const k = 0.3 + wr() * 0.45;
      const gap = box(roof.grp, std('#120e0b', { roughness: 1 }), 0.09 + wr() * 0.08, 0.01, 0.07, (wr() - 0.5) * 0.7, rh * (1 - k) + 0.04, side * ((D + 0.02) / 2) * k);
      gap.rotation.x = side * roof.angle;
    }
  }
  // The gear on the gable (+X): the artificer's sign.
  const sign = G(g, right + 0.02, yUp + hUp + 0.11, Z);
  sign.rotation.y = Math.PI / 2;
  gear(sign, 0.085, 10, iron ? PLATE : COPPER, 0.025, IRON);
  // Chimney at the back corner, a copper cowl on top (crumbling: snapped off, its top at the foot).
  const chx = X - W / 2 + 0.12, chz = Z - D / 2 + 0.12;
  const chTop = wear === 2 ? 1.0 : 1.3;
  box(g, wallM, 0.17, chTop - yUp, 0.17, chx, (yUp + chTop) / 2, chz);
  if (wear === 2) {
    snapTop(g, chx, chTop, chz, 0.07, wallM, wr);
    const top = box(g, wallM, 0.17, 0.2, 0.17, chx - 0.5, 0.1, chz + 0.45);
    top.rotation.set(0.3, 0.6, Math.PI / 2);
  } else {
    box(g, wallM, 0.21, 0.04, 0.21, chx, chTop, chz);
    box(g, BLACK, 0.12, 0.02, 0.12, chx, chTop + 0.015, chz);
    for (const k of [-1, 1]) limb(g, V(chx + k * 0.07, chTop + 0.02, chz), V(chx + k * 0.05, chTop + 0.1, chz), 0.008, 0.008, COPPER, 4);
    const cowl = M(new THREE.ConeGeometry(0.1, 0.07, 4), COPPER, g);
    cowl.position.set(chx, chTop + 0.13, chz);
    cowl.rotation.y = Math.PI / 4;
  }
  if (iron) for (const y of [0.85, 1.15]) if (y < chTop - 0.05) box(g, IRON_BAND, 0.185, 0.03, 0.185, chx, y, chz);
  // Front: the open double doors and the forge glowing inside.
  const dx = X + 0.06;
  box(g, BLACK, 0.3, 0.29, 0.02, dx, yLo + 0.145, front + 0.004);
  box(g, FORGE, 0.16, 0.06, 0.012, dx - 0.04, yLo + 0.05, front + 0.006);
  box(g, win, 0.26, 0.12, 0.01, dx, yLo + 0.09, front + 0.007);
  glowSprite(g, '#ff7a2a', 0.42, V(dx, yLo + 0.12, front + 0.06), 0.5);
  box(g, beam, 0.38, 0.05, 0.05, dx, yLo + 0.31, front + 0.012);
  for (const k of [-1, 1]) {
    const leaf = G(g, dx + k * 0.15, yLo + 0.145, front + 0.01);
    leaf.rotation.y = k * -1.15;
    box(leaf, mat === 1 ? WOOD : DWOOD, 0.15, 0.28, 0.02, k * 0.075, 0, 0.012);
    for (const y of [-0.08, 0.08]) box(leaf, IRON_BAND, 0.14, 0.022, 0.006, k * 0.07, y, 0.024);
  }
  // Forge vent and bellows left of the doors, a copper flue climbing to the roof.
  const vx = X - 0.27;
  box(g, wallM, 0.16, 0.16, 0.04, vx, yLo + 0.11, front + 0.02);
  box(g, FORGE, 0.11, 0.09, 0.012, vx, yLo + 0.1, front + 0.042);
  for (const x of [-0.035, 0, 0.035]) box(g, IRON, 0.012, 0.1, 0.012, vx + x, yLo + 0.1, front + 0.05);
  glowSprite(g, '#ff6a1a', 0.3, V(vx, yLo + 0.1, front + 0.08), 0.55);
  const flue = new THREE.CatmullRomCurve3([V(vx + 0.05, yLo + 0.19, front + 0.035), V(vx + 0.05, yUp + 0.1, front + 0.035), V(vx + 0.05, yUp + hUp - 0.02, front + 0.04), V(vx + 0.05, yUp + hUp + 0.08, front - 0.06)]);
  M(new THREE.TubeGeometry(flue, 10, 0.016, 6), COPPER, g);
  for (const y of [yLo + 0.32, yUp + 0.15]) box(g, IRON, 0.05, 0.015, 0.02, vx + 0.05, y, front + 0.02);
  const bel = G(g, vx - 0.12, yLo, front + 0.13);
  bel.rotation.y = 0.5;
  box(bel, WOOD, 0.16, 0.018, 0.09, 0, 0.03, 0);
  const top = box(bel, WOOD, 0.16, 0.018, 0.09, 0, 0.085, 0);
  top.rotation.z = -0.22;
  box(bel, leather('#2e1d12'), 0.12, 0.05, 0.08, -0.01, 0.055, 0);
  const noz = M(new THREE.ConeGeometry(0.018, 0.08, 6), COPPER, bel);
  noz.rotation.z = -Math.PI / 2;
  noz.position.set(0.11, 0.045, 0);
  // The great flywheel in the yard right of the doors (part 'spin', its axle out toward the field), on a
  // bearing post against the wall, belted up to a pulley under the eaves.
  const fwX = 0.34, fwY = 0.31, fwZ = front + 0.19;
  box(g, wallM, 0.08, fwY - yLo, 0.06, fwX, (fwY + yLo) / 2, fwZ - 0.08);
  box(g, IRON, 0.09, 0.05, 0.07, fwX, fwY, fwZ - 0.08);
  const axle = M(new THREE.CylinderGeometry(0.016, 0.016, 0.16, 6), PLATE_HI, g);
  axle.rotation.x = Math.PI / 2;
  axle.position.set(fwX, fwY, fwZ - 0.04);
  const fw = part(G(g, fwX, fwY, fwZ), 'spin');
  fw.rotation.x = Math.PI / 2;
  const rimM = mat === 1 ? DWOOD : IRON;
  const rim = M(new THREE.TorusGeometry(0.2, 0.026, 6, 28), rimM, fw);
  rim.rotation.x = Math.PI / 2;
  const tire = M(new THREE.TorusGeometry(0.2, 0.013, 4, 28), PLATE, fw);
  tire.rotation.x = Math.PI / 2;
  tire.scale.z = 2.3;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const sp = box(fw, rimM, 0.028, 0.022, 0.19, Math.sin(a) * 0.095, 0, Math.cos(a) * 0.095);
    sp.rotation.y = a;
  }
  M(new THREE.CylinderGeometry(0.048, 0.048, 0.065, 10), COPPER, fw);
  M(new THREE.CylinderGeometry(0.022, 0.022, 0.08, 6), PLATE_HI, fw);
  // Two counterweights on the rim, copper, and a crank pin.
  for (const a of [0.5, 0.5 + Math.PI]) box(fw, COPPER, 0.065, 0.05, 0.05, Math.sin(a) * 0.2, 0, Math.cos(a) * 0.2, a);
  M(new THREE.CylinderGeometry(0.012, 0.012, 0.06, 5), PLATE_HI, fw).position.set(0.13, -0.03, 0);
  // The belt from the hub up to a pulley under the eaves.
  const pul = V(fwX, yUp + 0.16, front + 0.04);
  box(g, IRON, 0.06, 0.05, 0.05, fwX, pul.y + 0.04, front + 0.02);
  const pw = M(new THREE.CylinderGeometry(0.038, 0.038, 0.03, 10), COPPER, g);
  pw.rotation.x = Math.PI / 2;
  pw.position.copy(pul);
  const belt = leather('#2e1d12');
  for (const k of [-1, 1]) limb(g, V(fwX + k * 0.046, fwY, fwZ - 0.01), V(fwX + k * 0.036, pul.y, pul.z + 0.01), 0.006, 0.006, belt, 4);
  // The crane at the back-right corner: a mast, a braced jib out over the yard, a windlass and a gear on the chain.
  const mastM = iron ? IRON : DWOOD;
  const mx = 0.49, mz = -0.36, mTop = 1.16;
  box(g, PLINTH, 0.13, 0.05, 0.13, mx, 0.105, mz);
  box(g, mastM, 0.065, mTop - 0.08, 0.065, mx, (mTop + 0.08) / 2, mz);
  for (const y of [0.35, 0.75, 1.05]) box(g, iron ? PLATE : IRON, 0.075, 0.025, 0.075, mx, y, mz);
  const root = V(mx, mTop - 0.05, mz);
  const tip = V(0.5, 1.06, 0.1);
  if (wear === 2) {
    // The jib snapped at the brace: the outer half hangs down from it, its load fallen in the yard.
    const mid = root.clone().lerp(tip, 0.45);
    limb(g, root, mid, 0.026, 0.024, mastM, 5);
    snapTop(g, mid.x, mid.y - 0.02, mid.z, 0.024, mastM, wr);
    limb(g, mid.clone().add(V(0, -0.05, 0.01)), V(0.53, 0.14, 0.12), 0.024, 0.02, mastM, 5);
    limb(g, V(mx, 0.75, mz), mid.clone().add(V(0, -0.02, 0)), 0.015, 0.015, mastM, 5);
    const fallenGear = G(g, 0.5, 0.1, 0.26);
    fallenGear.rotation.x = -Math.PI / 2 + 0.25;
    gear(fallenGear, 0.1, 11, iron ? PLATE : COPPER, 0.035, IRON);
  } else {
    limb(g, root, tip, 0.026, 0.02, mastM, 5);
    limb(g, V(mx, 0.75, mz), root.clone().lerp(tip, 0.55), 0.015, 0.015, mastM, 5);
    // A pulley at the tip, the chain and its load: a gear being lowered onto the automaton.
    const pl = M(new THREE.CylinderGeometry(0.03, 0.03, 0.02, 10), IRON, g);
    pl.rotation.z = Math.PI / 2;
    pl.position.copy(tip).setY(tip.y - 0.02);
    const hook = V(tip.x, 0.7, tip.z);
    for (let i = 0; i < 6; i++) {
      const y = tip.y - 0.05 - i * ((tip.y - 0.05 - hook.y) / 6);
      const lk = M(new THREE.TorusGeometry(0.012, 0.004, 4, 8), DSTEEL, g);
      lk.position.set(tip.x, y - 0.03, tip.z);
      lk.rotation.y = i % 2 ? Math.PI / 2 : 0;
    }
    const hk = M(new THREE.TorusGeometry(0.022, 0.006, 4, 10, Math.PI * 1.4), DSTEEL, g);
    hk.position.set(hook.x, hook.y - 0.02, hook.z);
    const load = G(g, hook.x, hook.y - 0.13, hook.z);
    load.rotation.y = 1.2;
    gear(load, 0.1, 11, iron ? PLATE : COPPER, 0.035, IRON);
  }
  limb(g, V(mx - 0.01, root.y - 0.02, mz + 0.02), V(mx - 0.07, 0.3, mz + 0.06), 0.004, 0.004, DSTEEL, 3);
  const drum = M(new THREE.CylinderGeometry(0.035, 0.035, 0.12, 10), WOOD, g);
  drum.rotation.x = Math.PI / 2;
  drum.position.set(mx - 0.08, 0.28, mz + 0.08);
  for (const k of [-1, 1]) box(g, beam, 0.03, 0.2, 0.03, mx - 0.08, 0.18, mz + 0.08 + k * 0.07);
  limb(g, V(mx - 0.08, 0.28, mz + 0.16), V(mx - 0.08, 0.35, mz + 0.21), 0.006, 0.006, IRON, 4);
  // The unfinished automaton on its trestle, chest open on a cyan core.
  const au = G(g, 0.5, 0.08, 0.08);
  au.rotation.y = 0.5;
  for (const k of [-1, 1]) {
    limb(au, V(k * 0.07, 0, -0.04), V(k * 0.05, 0.13, 0), 0.012, 0.012, WOOD, 4);
    limb(au, V(k * 0.07, 0, 0.04), V(k * 0.05, 0.13, 0), 0.012, 0.012, WOOD, 4);
  }
  box(au, WOOD, 0.14, 0.02, 0.06, 0, 0.135, 0);
  M(new THREE.CylinderGeometry(0.065, 0.055, 0.13, 8), IRON, au).position.y = 0.21;
  box(au, PLATE, 0.11, 0.09, 0.02, 0, 0.225, 0.05);
  const chest = box(au, PLATE, 0.05, 0.08, 0.012, 0.075, 0.22, 0.06);
  chest.rotation.y = -1.0;
  M(new THREE.SphereGeometry(0.02, 8, 6), CORE, au).position.set(0.02, 0.21, 0.062);
  glowSprite(au, CYAN, 0.16, V(0.02, 0.21, 0.08), 0.7);
  box(au, IRON, 0.07, 0.06, 0.07, 0, 0.31, 0);
  box(au, emis('#d9fbff', '#3fd4ff', 1.2), 0.04, 0.012, 0.01, 0, 0.315, 0.036);
  limb(au, V(-0.075, 0.25, 0), V(-0.09, 0.16, 0.03), 0.018, 0.015, PLATE, 5);
  M(new THREE.SphereGeometry(0.026, 6, 5), COPPER, au).position.set(-0.075, 0.26, 0);
  // Workbench: a vise, a hammer, parts and a lit core.
  const wb = G(g, -0.36, 0.08, 0.38);
  wb.rotation.y = 0.15;
  box(wb, WOOD, 0.32, 0.03, 0.13, 0, 0.17, 0);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(wb, DWOOD, 0.025, 0.17, 0.025, sx * 0.14, 0.085, sz * 0.05);
  box(wb, DWOOD, 0.28, 0.02, 0.02, 0, 0.05, 0);
  box(wb, IRON, 0.05, 0.05, 0.05, 0.12, 0.21, 0.02);
  box(wb, PLATE_HI, 0.012, 0.012, 0.06, 0.12, 0.225, 0.06);
  const hm = G(wb, -0.05, 0.19, 0.02);
  hm.rotation.y = 0.6;
  box(hm, WOOD, 0.12, 0.012, 0.012, 0, 0, 0);
  box(hm, DSTEEL, 0.02, 0.025, 0.04, -0.06, 0.005, 0);
  const lg = G(wb, 0.03, 0.19, -0.02);
  lg.rotation.x = -Math.PI / 2;
  gear(lg, 0.035, 8, COPPER, 0.012, IRON);
  M(new THREE.SphereGeometry(0.014, 6, 5), CORE, wb).position.set(-0.11, 0.195, -0.02);
  glowSprite(wb, CYAN, 0.09, V(-0.11, 0.2, -0.02), 0.6);
  // A coil of copper wire by the crane's foot, a cask and a stack of plate against the left wall.
  const coil = M(new THREE.TorusGeometry(0.05, 0.016, 5, 12), COPPER, g);
  coil.rotation.x = Math.PI / 2;
  coil.position.set(0.5, 0.1, -0.16);
  const cask = M(new THREE.CylinderGeometry(0.05, 0.05, 0.12, 9), WOOD, g);
  cask.position.set(-0.56, 0.14, 0.08);
  ring(g, 0.052, 0.006, IRON, 0.11, 3, 12).position.set(-0.56, 0.11, 0.08);
  ring(g, 0.052, 0.006, IRON, 0.17, 3, 12).position.set(-0.56, 0.17, 0.08);
  for (let i = 0; i < 3; i++) box(g, PLATE, 0.16, 0.012, 0.12, -0.56, 0.09 + i * 0.014, -0.2, 0.1 * i);
  if (iron) {
    // Riveted plates over the ground floor, straps and angle irons up the corners.
    boxClad(g, X, Z, W, D, yLo, yUp + hUp, [yUp - 0.01, yUp + hUp - 0.02], yLo + 0.2, ['l', 'b']);
    for (const x of [X - W / 2 + 0.12, X + 0.32]) plate(g, x, yLo + 0.11, front + 0.009, 0.16, 0.18, 0);
    for (const z of [Z - 0.2, Z + 0.21]) plate(g, right + 0.009, yLo + 0.11, z, 0.14, 0.18, Math.PI / 2);
  }
  if (wear > 0) {
    let k = 0;
    for (const [x, z, ry] of [[X - 0.32, front + 0.004, 0], [X + 0.3, front + 0.004, 0], [right + 0.004, Z - 0.18, Math.PI / 2], [X, Z - D / 2 - 0.004, Math.PI]] as const) {
      if (k++ < 2 || wr() < 0.5 * wear) crack(g, x, yUp - 0.04, z, ry, wear === 2 ? 0.34 : 0.24, wr);
      if (k < 3 || wr() < 0.4 * wear) stain(g, x + 0.05, yLo + 0.15, z + (ry === 0 ? 0.002 : 0), ry, 0.07 + wr() * 0.04, wr, '#26221f');
    }
    stain(g, dx, yLo + 0.33, front + 0.03, 0, 0.1, wr, '#1e1a17');
    rubbleAround(g, wear === 2 ? 11 : 5, 0.62, wr, mat === 1 ? ['#77736c', '#6b675f', '#837e75'] : iron ? ['#72767c', '#5d6167', '#868a90'] : ASHLAR, X, Z);
    splinters(g, wear * 3, 0.6, wr, X, Z);
  }
  return done(c, 1.4);
};

/* ---------- clockwork turret ---------- */

/** Face index helpers for an 8-sided drum turned π/8: face i looks along angle i·π/4 (0 = +Z). */
const faceDir = (a: number): THREE.Vector3 => V(Math.sin(a), 0, Math.cos(a));

/**
 * The workshop's clockwork turret: a compact geared tower with a rotating head (part 'turret') that rattles
 * off light bolts from a drum magazine through a short steel prod. Its base is a timber trestle (wood),
 * an octagonal stone drum (stone) or a riveted iron drum (iron); on its flank a great copper gear turns
 * (part 'spin') and a blued mainspring coils in its housing; a winding key stands out of the head's back.
 * Level 1 one bolt guide, 2 two and side armor, 3 three and a heavier drum and a pennant, 4 four.
 */
export const clockworkTower = (o?: ClockworkOpts): Model => {
  const level = o?.level ?? 1;
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.62, 83);
  const { g } = c;
  const wr = mulberry32(830 + mat * 5 + wear);
  const iron = mat === 3;
  const top = 0.8;
  const st = stone(iron ? IRON_STONE : '#8b857a');
  // The face the gear sits on (+X +Z diagonal) and the mainspring's (−X +Z).
  const aGear = Math.PI / 4;
  const aSpring = -Math.PI / 4;
  let apGear: number;
  let apSpring: number;
  const yGear = 0.4;
  if (mat === 1) {
    // Timber trestle: four raked legs, X-braces, an iron-shod deck; the drive shaft and gearbox inside.
    const leg = (sx: number, sz: number): [THREE.Vector3, THREE.Vector3] => [V(sx * 0.34, 0, sz * 0.34), V(sx * 0.25, top - 0.06, sz * 0.25)];
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      const [a, b] = leg(sx, sz);
      limb(g, a, b, 0.05, 0.042, WOOD, 6);
      box(g, IRON, 0.11, 0.05, 0.11, a.x, 0.025, a.z);
    }
    const braces: [number, number, number, number][] = [[-1, 1, 1, 1], [1, 1, 1, -1], [1, -1, -1, -1], [-1, -1, -1, 1]];
    for (const [ax, az, bx, bz] of braces) {
      const [a0, a1] = leg(ax, az);
      const [b0, b1] = leg(bx, bz);
      const lo = 0.08 / (top - 0.06);
      const hi = (top - 0.12) / (top - 0.06);
      // Breaking (crumbling): one brace on the camera side is gone, its stubs left.
      if (wear === 2 && ax === -1 && az === 1) {
        snapTop(g, a0.x + (a1.x - a0.x) * lo, 0.1, a0.z + (a1.z - a0.z) * lo, 0.02, WOOD, wr);
        continue;
      }
      limb(g, a0.clone().lerp(a1, lo), b0.clone().lerp(b1, hi), 0.018, 0.018, DWOOD, 5);
      limb(g, a0.clone().lerp(a1, hi), b0.clone().lerp(b1, lo), 0.018, 0.018, DWOOD, 5);
    }
    box(g, DWOOD, 0.66, 0.07, 0.66, 0, top - 0.035, 0);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, IRON, 0.08, 0.075, 0.08, sx * 0.3, top - 0.035, sz * 0.3);
    M(new THREE.CylinderGeometry(0.05, 0.06, top - 0.07, 8), IRON, g).position.y = (top - 0.07) / 2;
    box(g, IRON, 0.22, 0.2, 0.22, 0, yGear, 0, Math.PI / 4);
    box(g, PLATE, 0.24, 0.03, 0.24, 0, yGear + 0.1, 0, Math.PI / 4);
    apGear = 0.11 * Math.SQRT2 * 0.72 + 0.04;
    apSpring = apGear;
    // A ladder up the back.
    for (const k of [-1, 1]) limb(g, V(k * 0.08, 0, -0.42), V(k * 0.08, top, -0.28), 0.012, 0.012, DWOOD, 4);
    for (let i = 1; i < 6; i++) {
      const t = i / 6;
      box(g, DWOOD, 0.16, 0.012, 0.016, 0, top * t, -0.42 + 0.14 * t);
    }
  } else {
    // Octagonal drum with an arched recess on the gear face, a string course and an iron crown ring.
    const drum = M(new THREE.CylinderGeometry(0.36, 0.46, top, 8), st, g);
    drum.position.y = top / 2;
    drum.rotation.y = Math.PI / 8;
    box(g, PLINTH, 0.98, 0.06, 0.98, 0, 0.03, 0, Math.PI / 8).scale.set(0.9, 1, 0.9);
    const rAt = (y: number): number => 0.46 - (0.1 * y) / top;
    apGear = rAt(yGear) * Math.cos(Math.PI / 8);
    apSpring = apGear;
    // The recess behind the gear: dark, with the shaft's glint.
    const rec = G(g, Math.sin(aGear) * (apGear - 0.01), yGear, Math.cos(aGear) * (apGear - 0.01));
    rec.rotation.y = aGear;
    box(rec, BLACK, 0.24, 0.3, 0.02, 0, 0, 0);
    box(rec, st, 0.29, 0.05, 0.04, 0, 0.17, 0.01);
    const course = M(new THREE.CylinderGeometry(0.405, 0.405, 0.04, 8), PLINTH, g);
    course.position.y = 0.56;
    course.rotation.y = Math.PI / 8;
    const crown = M(new THREE.CylinderGeometry(0.41, 0.37, 0.07, 8), st, g);
    crown.position.y = top - 0.035;
    crown.rotation.y = Math.PI / 8;
    // A small iron door.
    const ad = rAt(0.2) * Math.cos(Math.PI / 8) + 0.004;
    const door = G(g, Math.sin(Math.PI * 0.75) * ad, 0.2, Math.cos(Math.PI * 0.75) * ad);
    door.rotation.y = Math.PI * 0.75;
    box(door, DWOOD, 0.14, 0.24, 0.02, 0, 0, 0);
    for (const y of [-0.07, 0.07]) box(door, IRON, 0.15, 0.02, 0.008, 0, y, 0.012);
    if (iron) {
      roundClad(g, 0.46, 0.36, 0, top, 8, Math.PI / 8, 0.28, [0.33, top - 0.12], [0]);
      const rim = M(new THREE.CylinderGeometry(0.42, 0.42, 0.04, 8, 1, true), IRON_BAND, g);
      rim.position.y = top - 0.035;
      rim.rotation.y = Math.PI / 8;
    } else if (level >= 2) {
      ring(g, rAt(0.12) + 0.012, 0.016, COPPER, 0.12, 4, 8).rotation.z = Math.PI / 8;
    }
    // A copper steam pipe up the front face, flanged, with a valve wheel.
    const pipe = (y: number): THREE.Vector3 => V(0, y, rAt(y) * Math.cos(Math.PI / 8) + 0.035);
    limb(g, pipe(0.08), pipe(top - 0.06), 0.017, 0.017, COPPER, 6);
    for (const y of [0.12, 0.45, top - 0.1]) {
      const fl = M(new THREE.CylinderGeometry(0.026, 0.026, 0.02, 8), COPPER, g);
      fl.position.copy(pipe(y));
      box(g, IRON, 0.03, 0.02, 0.04, 0, y, pipe(y).z - 0.025);
    }
    const valve = G(g, 0, 0.27, pipe(0.27).z + 0.04);
    M(new THREE.TorusGeometry(0.035, 0.007, 4, 12), IRON, valve);
    for (const a of [0, Math.PI / 2]) box(valve, IRON, 0.07, 0.008, 0.008, 0, 0, 0).rotation.z = a;
    limb(g, pipe(0.27), V(0, 0.27, pipe(0.27).z + 0.04), 0.008, 0.008, IRON, 4);
    if (wear > 0) {
      for (let i = 0; i < wear * 2 + 1; i++) crackRound(g, rAt(0.5) * Math.cos(Math.PI / 8), 0.65 - wr() * 0.2, -0.6 + i * 0.5 + wr() * 0.2, wear === 2 ? 0.4 : 0.25, wr);
      for (let i = 0; i < wear * 2; i++) {
        const a = -0.8 + wr() * 1.6;
        stain(g, Math.sin(a) * 0.41, 0.25 + wr() * 0.4, Math.cos(a) * 0.41, a, 0.08 + wr() * 0.04, wr);
      }
    }
  }
  // The great gear (part 'spin', its axle out of the face) with a pinion on the same arbor.
  const gp = part(G(g, Math.sin(aGear) * (apGear + 0.03), yGear, Math.cos(aGear) * (apGear + 0.03)), 'spin');
  gp.quaternion.setFromUnitVectors(V(0, 1, 0), faceDir(aGear));
  const gr = G(gp);
  gr.rotation.x = Math.PI / 2;
  gear(gr, mat === 1 ? 0.13 : 0.15, 12, COPPER, 0.035, IRON);
  const pin = G(gp, 0, 0.035, 0);
  pin.rotation.x = Math.PI / 2;
  gear(pin, 0.065, 8, IRON, 0.03, IRON);
  M(new THREE.CylinderGeometry(0.018, 0.018, 0.09, 6), PLATE_HI, gp).position.y = 0.03;
  // A second, idle gear meshing below it (crumbling: knocked off, lying at the foot).
  const idle = G(g);
  if (wear === 2) {
    idle.position.set(0.55, 0.035, 0.38);
    idle.rotation.set(-Math.PI / 2, 0, 0.4);
  } else {
    const n = faceDir(aGear);
    const t = V(Math.cos(aGear), 0, -Math.sin(aGear));
    idle.position.copy(n.clone().multiplyScalar(apGear + 0.02)).addScaledVector(t, -0.16).setY(yGear - 0.15);
    idle.quaternion.setFromUnitVectors(V(0, 0, 1), n);
  }
  gear(idle, 0.08, 9, iron ? PLATE : IRON, 0.03, IRON);
  // The mainspring: a blued steel coil in a copper housing, its arbor squared for the key.
  const ms = G(g, Math.sin(aSpring) * (apSpring + 0.01), yGear + 0.02, Math.cos(aSpring) * (apSpring + 0.01));
  ms.rotation.y = aSpring;
  const housing = M(new THREE.TorusGeometry(0.115, 0.016, 5, 20), COPPER, ms);
  housing.position.z = 0.012;
  M(new THREE.CylinderGeometry(0.12, 0.12, 0.012, 16), IRON, ms).rotation.x = Math.PI / 2;
  const sp: THREE.Vector3[] = [];
  for (let i = 0; i <= 64; i++) {
    const t = i / 64;
    const a = t * Math.PI * 2 * 3.4;
    const r = 0.022 + t * 0.085;
    sp.push(V(Math.cos(a) * r, Math.sin(a) * r, 0.016));
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(sp), 80, 0.0055, 4), BLUED, ms);
  box(ms, PLATE_HI, 0.03, 0.03, 0.03, 0, 0, 0.02, Math.PI / 4);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    rivet(ms, Math.cos(a) * 0.115, Math.sin(a) * 0.115, 0.03, 0.01);
  }
  // Deck: a turret ring, and an iron railing round it (level 3: spikes on the posts).
  M(new THREE.CylinderGeometry(0.28, 0.3, 0.06, 14), IRON, g).position.y = top + 0.03;
  if (wear < 2 || mat === 1) {
    const rr = mat === 1 ? 0.31 : 0.37;
    ring(g, rr, 0.01, IRON, top + 0.14, 4, 24);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      limb(g, V(Math.sin(a) * rr, top, Math.cos(a) * rr), V(Math.sin(a) * rr, top + 0.15, Math.cos(a) * rr), 0.009, 0.009, IRON, 4);
      if (level >= 3) M(new THREE.ConeGeometry(0.016, 0.06, 4), IRON, g).position.set(Math.sin(a) * rr, top + 0.18, Math.cos(a) * rr);
    }
  }
  if (level >= 3) {
    limb(g, V(-0.22, top, -0.24), V(-0.22, top + 0.62, -0.24), 0.01, 0.01, IRON, 5);
    const pen = M(new THREE.BoxGeometry(0.2, 0.12, 0.01), cloth('#7a1d22'), g);
    pen.position.set(-0.12, top + 0.55, -0.24);
    M(new THREE.BoxGeometry(0.05, 0.05, 0.014), cloth('#d9dce3'), g).position.set(-0.1, top + 0.55, -0.24);
  }
  // The head (part 'turret').
  const t = part(G(g, 0, top + 0.06, 0), 'turret');
  t.scale.setScalar(1.22);
  M(new THREE.CylinderGeometry(0.24, 0.26, 0.05, 12), PLATE, t).position.y = 0.025;
  const hw = 0.3, hh = 0.16, hd = 0.3;
  box(t, PLATE, hw, hh, hd, 0, 0.05 + hh / 2, -0.02);
  const glacis = box(t, PLATE, hw, 0.12, 0.03, 0, 0.18, 0.12);
  glacis.rotation.x = -0.75;
  box(t, IRON, hw + 0.01, 0.025, hd + 0.01, 0, 0.06, -0.02);
  for (const k of [-1, 1]) for (const z of [-0.12, 0, 0.1]) rivet(t, k * (hw / 2 + 0.003), 0.18, z - 0.02);
  if (level >= 2) {
    for (const k of [-1, 1]) {
      const sh = box(t, IRON, 0.022, 0.17, 0.27, k * (hw / 2 + 0.018), 0.14, 0);
      sh.rotation.y = k * 0.08;
      for (const z of [-0.09, 0.09]) rivet(t, k * (hw / 2 + 0.031), 0.19, z);
    }
  }
  // Drum magazine on top, banded in copper, bolt holes on its face.
  const dr = level >= 3 ? 0.1 : 0.085;
  const mag = G(t, 0, 0.21 + dr * 0.6, -0.04);
  M(new THREE.CylinderGeometry(dr, dr, 0.15, 12), IRON, mag).rotation.x = Math.PI / 2;
  for (const z of [-0.055, 0.055]) {
    const b = M(new THREE.TorusGeometry(dr + 0.003, 0.008, 4, 16), COPPER, mag);
    b.position.z = z;
  }
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const h = M(new THREE.CircleGeometry(0.014, 6), IRON, mag);
    h.position.set(Math.cos(a) * dr * 0.6, Math.sin(a) * dr * 0.6, 0.0765);
  }
  M(new THREE.CylinderGeometry(0.02, 0.02, 0.17, 6), PLATE_HI, mag).rotation.x = Math.PI / 2;
  // The prod: a short steel bow across the front, strung.
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 10; i++) {
    const a = -1 + (i / 10) * 2;
    pts.push(V(Math.sin(a) * (0.2 + level * 0.012), 0.12, 0.22 - Math.cos(a) * 0.07));
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 12, 0.012, 5), PLATE_HI, t);
  limb(t, pts[0]!, V(0, 0.12, 0.1), 0.003, 0.003, IRON, 3);
  limb(t, pts[10]!, V(0, 0.12, 0.1), 0.003, 0.003, IRON, 3);
  // Bolt guides: one per level (four at level 4, in two rows), each with a bolt's steel head.
  const guides: [number, number][] =
    level >= 4 ? [[-0.05, 0.1], [0.05, 0.1], [-0.05, 0.155], [0.05, 0.155]] : level === 3 ? [[-0.07, 0.11], [0, 0.11], [0.07, 0.11]] : level === 2 ? [[-0.04, 0.11], [0.04, 0.11]] : [[0, 0.11]];
  for (const [x, y] of guides) {
    box(t, IRON, 0.032, 0.03, 0.24, x, y, 0.2);
    const tip = M(new THREE.ConeGeometry(0.014, 0.05, 5), PLATE_HI, t);
    tip.rotation.x = Math.PI / 2;
    tip.position.set(x, y + 0.02, 0.34);
  }
  // Rear: a copper boiler with its stack and gauge, and the winding key.
  const boiler = M(new THREE.CylinderGeometry(0.055, 0.055, 0.22, 10), COPPER, t);
  boiler.rotation.z = Math.PI / 2;
  boiler.position.set(0, 0.14, -0.2);
  for (const k of [-1, 1]) {
    const cap = M(new THREE.SphereGeometry(0.055, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), IRON, t);
    cap.rotation.z = -k * Math.PI / 2;
    cap.position.set(k * 0.11, 0.14, -0.2);
  }
  M(new THREE.CylinderGeometry(0.016, 0.02, 0.1, 6), IRON, t).position.set(0.07, 0.24, -0.2);
  M(new THREE.CylinderGeometry(0.025, 0.018, 0.02, 6), IRON, t).position.set(0.07, 0.3, -0.2);
  const gauge = M(new THREE.CylinderGeometry(0.022, 0.022, 0.012, 10), PLATE_HI, t);
  gauge.rotation.z = Math.PI / 2;
  gauge.position.set(-0.12, 0.2, -0.17);
  limb(t, V(0, 0.2, -0.24), V(0, 0.2, -0.33), 0.012, 0.012, IRON, 5);
  const key = G(t, 0, 0.2, -0.34);
  for (const k of [-1, 1]) {
    const lobe = M(new THREE.TorusGeometry(0.045, 0.014, 5, 12), COPPER, key);
    lobe.position.x = k * 0.05;
    lobe.scale.y = 0.8;
  }
  box(key, COPPER, 0.04, 0.03, 0.02, 0, 0, 0);
  // The eye: a cyan lens over the glacis.
  const lens = M(new THREE.CircleGeometry(0.03, 10), CORE, t);
  lens.position.set(0.09, 0.2, 0.135);
  lens.rotation.x = -0.75;
  const bezel = ring(t, 0.032, 0.007, COPPER, 0, 4, 12);
  bezel.position.set(0.09, 0.2, 0.137);
  bezel.rotation.set(-0.75 + Math.PI / 2, 0, 0);
  glowSprite(t, CYAN, 0.22, V(0.09, 0.21, 0.15), 0.75);
  if (wear > 0) {
    rubbleAround(g, wear === 2 ? 9 : 4, 0.48, wr, mat === 1 ? ['#5b3d26', '#3a271a'] : iron ? ['#72767c', '#5d6167'] : ASHLAR);
    if (mat === 1) splinters(g, wear * 4, 0.45, wr);
    // A plate sprung loose from the head, hanging off its rivet.
    if (wear === 2) {
      const lp = box(t, PLATE, 0.12, 0.08, 0.012, -0.12, 0.08, 0.16);
      lp.rotation.set(0.3, 0.5, 0.9);
    }
  }
  return done(c, top + 0.42);
};

/* ---------- the alchemist's laboratory ---------- */

/**
 * A stone laboratory with its gable to the front, a great round window lit sickly green, a verdigris dome
 * on the ridge and a tall chimney. In the yard a copper still bubbles on its hearth, its swan neck running
 * to a coiled condenser that drips into a glowing flask; a lean-to shelf of bottles in four brews, a carboy
 * of acid. Worn (1): cracks, soot, a cracked pane. Crumbling (2): the chimney broken, the dome knocked
 * askew, a flask shattered in a glowing puddle.
 */
export const lab = (o?: Era2Opts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.8, 85);
  const { g } = c;
  const wr = mulberry32(850 + mat * 3 + wear);
  const iron = mat === 3;
  const wallM = mat === 1 ? stone('#7c7872') : stone(iron ? IRON_STONE : '#918c84');
  const roofM = mat === 1 ? std('#3f3a36', { roughness: 0.95, flatShading: true }) : iron ? metal('#3d434b', { roughness: 0.42, metalness: 0.6, flatShading: true }) : stone('#3a3a40');
  const X = -0.14, Z = -0.14, W = 0.7, D = 0.62;
  const front = Z + D / 2;
  const right = X + W / 2;
  const y0 = 0.08, H = 0.52;
  box(g, PLINTH, 1.22, 0.08, 1.02, 0, 0.04, 0);
  box(g, wallM, W, H, D, X, y0 + H / 2, Z);
  if (mat === 1) {
    // Rubble masonry with timber lintels and corner posts.
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, DWOOD, 0.045, H, 0.045, X + (sx * W) / 2, y0 + H / 2, Z + (sz * D) / 2);
    box(g, DWOOD, W + 0.03, 0.04, D + 0.03, X, y0 + H - 0.03, Z);
    const fs = [PLINTH, stone('#837e75')];
    for (let i = 0; i < 12; i++) {
      const s = 0.03 + wr() * 0.03;
      const m = M(new THREE.DodecahedronGeometry(s, 0), fs[i % 2] as THREE.Material, g);
      m.scale.z = 0.45;
      if (i < 6) m.position.set(X - W / 2 + 0.05 + wr() * (W - 0.1), y0 + 0.04 + wr() * (H - 0.1), front + 0.004);
      else {
        m.position.set(right + 0.004, y0 + 0.04 + wr() * (H - 0.1), Z - D / 2 + 0.05 + wr() * (D - 0.1));
        m.rotation.y = Math.PI / 2;
      }
    }
  } else {
    quoins(g, stone(iron ? '#868a90' : '#a8a298'), X, Z, W, D, y0, y0 + H);
    box(g, stone(iron ? '#5d6167' : '#6f6a63'), W + 0.03, 0.035, D + 0.03, X, y0 + H, Z);
  }
  // Roof: ridge running front to back, so the gable faces the field.
  const rh = 0.34;
  const roof = pitchedRoof(g, X, y0 + H, Z, W + 0.02, rh, D + 0.02, roofM, wallM, true);
  if (iron) seams(roof.grp, W + 0.02, rh, D + 0.08, roof.angle);
  // The round window in the gable, lit sickly green-yellow (worn: a cracked pane).
  const wy = y0 + H + 0.1;
  box(g, BLACK, 0.22, 0.012, 0.012, X, wy - 0.12, front + 0.006);
  const frame = M(new THREE.TorusGeometry(0.085, 0.022, 5, 18), stone(iron ? '#868a90' : '#a8a298'), g);
  frame.position.set(X, wy, front + 0.012);
  const pane = M(new THREE.CircleGeometry(0.08, 18), emis('#e6f79a', BREW.acid, [1.1, 0.8, 0.45][wear] as number, { bumpScale: 0 }), g);
  pane.position.set(X, wy, front + 0.008);
  for (const a of [0, Math.PI / 2]) box(g, IRON, 0.16, 0.012, 0.01, X, wy, front + 0.014).rotation.z = a;
  if (wear > 0) box(g, BLACK, 0.09, 0.006, 0.006, X + 0.02, wy + 0.02, front + 0.016).rotation.z = 0.7;
  glowSprite(g, BREW.acid, 0.42, V(X, wy, front + 0.06), 0.5);
  // Arched door below it.
  const door = G(g, X, y0, front + 0.006);
  box(door, BLACK, 0.18, 0.27, 0.01, 0, 0.135, 0);
  box(door, DWOOD, 0.15, 0.24, 0.015, 0, 0.12, 0.006);
  for (const y of [0.06, 0.18]) box(door, IRON, 0.16, 0.018, 0.008, 0, y, 0.016);
  const arch = M(new THREE.CylinderGeometry(0.09, 0.09, 0.03, 10, 1, false, 0, Math.PI), wallM, door);
  arch.rotation.set(Math.PI / 2, 0, -Math.PI / 2);
  arch.position.set(0, 0.27, 0.004);
  box(door, FORGE, 0.03, 0.04, 0.01, 0.045, 0.3, 0.02);
  // Narrow windows on the right side, a green glow.
  for (const z of [Z - 0.12, Z + 0.12]) {
    box(g, BLACK, 0.012, 0.16, 0.06, right + 0.004, y0 + 0.3, z);
    box(g, brew(BREW.acid), 0.012, 0.1, 0.04, right + 0.006, y0 + 0.28, z);
  }
  // Verdigris dome on the ridge (crumbling: knocked askew).
  const dome = G(g, X, y0 + H + rh - 0.04, Z - 0.06);
  if (wear === 2) dome.rotation.set(0.25, 0, -0.3);
  M(new THREE.CylinderGeometry(0.1, 0.11, 0.12, 10), wallM, dome).position.y = 0.06;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    const sl = box(dome, brew(BREW.acid), 0.025, 0.06, 0.01, Math.sin(a) * 0.101, 0.07, Math.cos(a) * 0.101, a);
    sl.rotation.y = a;
  }
  M(new THREE.CylinderGeometry(0.12, 0.12, 0.02, 10), VERDIGRIS, dome).position.y = 0.13;
  M(new THREE.SphereGeometry(0.11, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), VERDIGRIS, dome).position.y = 0.13;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const rib = M(new THREE.TorusGeometry(0.111, 0.006, 3, 10, Math.PI / 2), COPPER, dome);
    rib.position.y = 0.13;
    rib.rotation.set(0, a, Math.PI / 2);
  }
  limb(dome, V(0, 0.23, 0), V(0, 0.34, 0), 0.008, 0.004, COPPER, 4);
  M(new THREE.SphereGeometry(0.018, 6, 5), COPPER, dome).position.y = 0.26;
  // The tall chimney at the back right (crumbling: snapped halfway).
  const chx = X + W / 2 - 0.1, chz = Z - D / 2 + 0.1;
  const chTop = wear === 2 ? 0.95 : 1.42;
  const chM = PLINTH;
  M(new THREE.CylinderGeometry(0.06, 0.075, chTop - 0.4, 8), chM, g).position.set(chx, (chTop + 0.4) / 2, chz);
  if (wear === 2) {
    snapTop(g, chx, chTop, chz, 0.06, chM, wr);
    const piece = M(new THREE.CylinderGeometry(0.06, 0.065, 0.32, 8), chM, g);
    piece.position.set(chx + 0.32, 0.07, chz - 0.3);
    piece.rotation.set(0, 0.8, Math.PI / 2);
  } else {
    M(new THREE.CylinderGeometry(0.085, 0.065, 0.06, 8), chM, g).position.set(chx, chTop, chz);
    M(new THREE.CylinderGeometry(0.05, 0.05, 0.012, 8), BLACK, g).position.set(chx, chTop + 0.031, chz);
    glowSprite(g, BREW.acid, 0.18, V(chx, chTop + 0.06, chz), 0.35);
  }
  for (const y of iron ? [0.7, 0.95, 1.2] : [0.95]) if (y < chTop - 0.05) ring(g, 0.07 - (y - 0.4) * 0.012, 0.01, IRON_BAND, y, 4, 10).position.set(chx, y, chz);
  // The yard: hearth and copper still, swan neck, condenser coil over a water cask, the flask.
  const hx = 0.4, hz = -0.02;
  box(g, stone('#5f5b55'), 0.26, 0.15, 0.22, hx, 0.155, hz);
  box(g, BLACK, 0.012, 0.08, 0.1, hx + 0.13, 0.13, hz);
  box(g, FORGE, 0.012, 0.045, 0.07, hx + 0.132, 0.115, hz);
  glowSprite(g, '#ff6a1a', 0.22, V(hx + 0.17, 0.13, hz), 0.55);
  const pot = M(new THREE.SphereGeometry(0.11, 12, 8), COPPER, g);
  pot.scale.y = 0.85;
  pot.position.set(hx, 0.32, hz);
  ring(g, 0.106, 0.01, COPPER, 0.3, 4, 16).position.set(hx, 0.3, hz);
  M(new THREE.CylinderGeometry(0.03, 0.06, 0.08, 10), COPPER, g).position.set(hx, 0.44, hz);
  M(new THREE.SphereGeometry(0.045, 10, 6), COPPER, g).position.set(hx, 0.5, hz);
  const neck = new THREE.CatmullRomCurve3([V(hx, 0.52, hz), V(hx + 0.05, 0.56, hz + 0.08), V(hx + 0.1, 0.52, hz + 0.2), V(hx + 0.12, 0.42, hz + 0.3)]);
  M(new THREE.TubeGeometry(neck, 14, 0.012, 6), COPPER, g);
  const cx = hx + 0.12, cz = hz + 0.32;
  const cask = M(new THREE.CylinderGeometry(0.075, 0.07, 0.2, 10), WOOD, g);
  cask.position.set(cx, 0.18, cz);
  for (const y of [0.11, 0.25]) ring(g, 0.076, 0.007, IRON, y, 3, 14).position.set(cx, y, cz);
  const water = M(new THREE.CircleGeometry(0.068, 12), IRON, g);
  water.position.set(cx, 0.281, cz);
  water.rotation.x = -Math.PI / 2;
  const coil: THREE.Vector3[] = [];
  for (let i = 0; i <= 40; i++) {
    const t = i / 40;
    const a = t * Math.PI * 2 * 3.5;
    coil.push(V(cx + Math.cos(a) * 0.045, 0.42 - t * 0.17, cz + Math.sin(a) * 0.045));
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(coil), 60, 0.008, 5), COPPER, g);
  limb(g, V(cx + 0.07, 0.17, cz + 0.02), V(cx + 0.11, 0.12, cz + 0.06), 0.007, 0.007, COPPER, 4);
  // The receiving flask (crumbling: shattered, a glowing puddle and shards).
  const fx = cx + 0.13, fz = cz + 0.08;
  if (wear === 2) {
    const puddle = M(new THREE.CircleGeometry(0.11, 10), brew(BREW.acid, 0.8), g);
    puddle.rotation.x = -Math.PI / 2;
    puddle.scale.y = 0.6;
    puddle.position.set(fx, 0.083, fz);
    for (let i = 0; i < 4; i++) {
      const sh = M(new THREE.TetrahedronGeometry(0.02, 0), GLASS, g);
      sh.position.set(fx + (wr() - 0.5) * 0.16, 0.09, fz + (wr() - 0.5) * 0.1);
    }
    glowSprite(g, BREW.acid, 0.22, V(fx, 0.1, fz), 0.5);
  } else {
    M(new THREE.SphereGeometry(0.045, 10, 8), brew(BREW.acid), g).position.set(fx, 0.12, fz);
    M(new THREE.SphereGeometry(0.055, 12, 8), GLASS, g).position.set(fx, 0.13, fz);
    M(new THREE.CylinderGeometry(0.015, 0.02, 0.06, 8), GLASS, g).position.set(fx, 0.2, fz);
    glowSprite(g, BREW.acid, 0.24, V(fx, 0.13, fz), 0.65);
  }
  // Lean-to shelf of bottles against the front wall, left of the door.
  const sx0 = X - 0.21, sz0 = front + 0.06;
  const lean = box(g, DWOOD, 0.26, 0.015, 0.14, sx0, y0 + 0.47, sz0 + 0.01);
  lean.rotation.x = 0.35;
  for (const k of [-1, 1]) box(g, DWOOD, 0.02, 0.46, 0.02, sx0 + k * 0.12, y0 + 0.23, sz0 + 0.06);
  const bottles = [BREW.acid, BREW.frost, BREW.fire, BREW.violet, BREW.acid, BREW.violet, BREW.frost];
  for (const [row, y] of [[0, y0 + 0.14], [1, y0 + 0.3]] as const) {
    box(g, WOOD, 0.26, 0.015, 0.09, sx0, y, sz0);
    for (let i = 0; i < 4; i++) {
      const col = bottles[(row * 3 + i) % bottles.length] as string;
      const bx = sx0 - 0.09 + i * 0.06;
      const tall = (i + row) % 2 === 0;
      M(new THREE.CylinderGeometry(0.018, 0.02, tall ? 0.07 : 0.05, 8), brew(col), g).position.set(bx, y + (tall ? 0.043 : 0.033), sz0 + 0.005);
      M(new THREE.CylinderGeometry(0.006, 0.008, 0.03, 5), GLASS, g).position.set(bx, y + (tall ? 0.09 : 0.07), sz0 + 0.005);
    }
  }
  glowSprite(g, BREW.violet, 0.16, V(sx0 + 0.03, y0 + 0.36, sz0 + 0.04), 0.5);
  glowSprite(g, BREW.frost, 0.14, V(sx0 - 0.03, y0 + 0.2, sz0 + 0.04), 0.5);
  // A big carboy of acid in a wicker cradle, and a crate of straw and flasks.
  const cb = G(g, -0.47, 0.08, 0.32);
  M(new THREE.SphereGeometry(0.075, 12, 8), brew(BREW.acid), cb).position.y = 0.09;
  M(new THREE.SphereGeometry(0.085, 12, 8), GLASS, cb).position.y = 0.09;
  M(new THREE.CylinderGeometry(0.088, 0.07, 0.07, 10), WOOD, cb).position.y = 0.035;
  M(new THREE.CylinderGeometry(0.02, 0.025, 0.05, 8), GLASS, cb).position.y = 0.19;
  glowSprite(cb, BREW.acid, 0.26, V(0, 0.1, 0.02), 0.5);
  const cr = G(g, -0.28, 0.08, 0.42);
  cr.rotation.y = 0.4;
  box(cr, WOOD, 0.14, 0.08, 0.1, 0, 0.04, 0);
  for (const [x, col] of [[-0.03, BREW.fire], [0.03, BREW.frost]] as const) M(new THREE.SphereGeometry(0.022, 8, 6), brew(col), cr).position.set(x, 0.1, 0);
  if (iron) {
    boxClad(g, X, Z, W, D, y0, y0 + H, [y0 + 0.24, y0 + H - 0.03], y0 + 0.2, ['l', 'b', 'r']);
    for (const x of [X - 0.22, X + 0.22]) plate(g, x, y0 + 0.11, front + 0.009, 0.15, 0.18, 0);
  }
  if (wear > 0) {
    let k = 0;
    for (const [x, z, ry] of [[X + 0.2, front + 0.004, 0], [right + 0.004, Z + 0.2, Math.PI / 2], [right + 0.004, Z - 0.2, Math.PI / 2], [X, Z - D / 2 - 0.004, Math.PI]] as const) {
      if (k++ < 2 || wr() < 0.5 * wear) crack(g, x, y0 + H - 0.05, z, ry, wear === 2 ? 0.36 : 0.25, wr);
      if (k < 3 || wr() < 0.4 * wear) stain(g, x - 0.04, y0 + 0.18, z, ry, 0.07 + wr() * 0.04, wr, '#2a2d22');
    }
    rubbleAround(g, wear === 2 ? 11 : 5, 0.6, wr, mat === 1 ? ['#7c7872', '#6b675f'] : iron ? ['#72767c', '#5d6167', '#868a90'] : ASHLAR, X, Z);
  }
  const L = pointLight(c, 0xc6e21a, 0.18, 1.2, V(X, wy, front + 0.25));
  const tick = (n: number): void => {
    L.intensity = (0.16 + (n % 5 < 2 ? 0.04 : 0)) * LIGHT_K;
  };
  return done(c, 1.45, tick);
};

/* ---------- the iron mines (incursion) ---------- */

const ORE = stone('#57392c');
const ORE_RUST = std('#74412c', { roughness: 0.85, flatShading: true });
const ORE_GLINT = metal('#4a4c52', { roughness: 0.3, metalness: 0.85, flatShading: true });
const CRAG = stone('#4e4c4c');
const CRAG_DK = stone('#3a3838');

/** A boulder of dark rock with a rust-red vein and a glint of iron in it. */
const vein = (parent: THREE.Object3D, x: number, y: number, z: number, s: number, rng: () => number, mat: THREE.Material = CRAG): void => {
  const r = M(new THREE.DodecahedronGeometry(s, 0), mat, parent);
  r.position.set(x, y, z);
  r.rotation.set(rng() * 3, rng() * 3, rng() * 3);
  r.scale.y = 0.75 + rng() * 0.3;
  const n = 2 + Math.floor(rng() * 3);
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2;
    const e = rng() * 0.8 - 0.1;
    const d = s * 0.92;
    const p = V(Math.cos(a) * Math.cos(e) * d, Math.sin(e) * d * r.scale.y, Math.sin(a) * Math.cos(e) * d);
    const k = M(new THREE.DodecahedronGeometry(s * (0.16 + rng() * 0.1), 0), i % 2 ? ORE_GLINT : ORE_RUST, parent);
    k.position.set(x + p.x, y + p.y, z + p.z);
    k.rotation.set(rng() * 3, rng() * 3, rng() * 3);
  }
};

/**
 * An iron mine at the clearing's heart: a dark crag veined with rust-red ore, a timbered adit with a
 * lantern, rails running out of it to an ore cart heaped with lumps, a pick left against the props.
 */
export const ironMine = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 87);
  const { g } = c;
  const rng = mulberry32(871);
  M(new THREE.CylinderGeometry(0.66, 0.74, 0.1, 9), stone('#3a3226'), g).position.y = 0.05;
  // The crag, highest at the back.
  const rocks: [number, number, number, number][] = [
    [0, 0.42, -0.32, 0.36], [-0.34, 0.3, -0.22, 0.27], [0.34, 0.3, -0.24, 0.28], [-0.2, 0.62, -0.42, 0.24], [0.2, 0.66, -0.4, 0.22],
    [0, 0.8, -0.38, 0.2], [-0.48, 0.16, 0.0, 0.17], [0.5, 0.15, -0.02, 0.16], [-0.42, 0.14, 0.22, 0.12],
  ];
  rocks.forEach(([x, y, z, s], i) => vein(g, x, y, z, s, rng, i % 3 === 2 ? CRAG_DK : CRAG));
  // The adit: a dark mouth framed by two sets of props and lintels.
  const ax = 0, az = -0.06;
  box(g, BLACK, 0.3, 0.36, 0.2, ax, 0.28, az - 0.06);
  for (const [dz, s] of [[0, 1], [-0.1, 0.92]] as const) {
    for (const k of [-1, 1]) {
      const p = limb(g, V(ax + k * 0.16 * s, 0.1, az + dz), V(ax + k * 0.14 * s, 0.46 * s + 0.04, az + dz), 0.026, 0.024, DWOOD, 6);
      p.rotation.z += k * 0.02;
    }
    box(g, DWOOD, 0.4 * s, 0.05, 0.06, ax, 0.48 * s + 0.03, az + dz);
  }
  for (const k of [-1, 1]) box(g, IRON, 0.012, 0.06, 0.07, ax + k * 0.12, 0.5, az + 0.005);
  // Lantern hung from the lintel.
  limb(g, V(ax + 0.12, 0.48, az + 0.04), V(ax + 0.12, 0.4, az + 0.06), 0.003, 0.003, DSTEEL, 3);
  box(g, IRON, 0.04, 0.05, 0.04, ax + 0.12, 0.37, az + 0.06);
  M(new THREE.SphereGeometry(0.016, 6, 5), emis('#ffd9a0', '#ff9a3a', 1.5), g).position.set(ax + 0.12, 0.37, az + 0.06);
  glowSprite(g, '#ffb060', 0.3, V(ax + 0.12, 0.37, az + 0.08), 0.75);
  // Rails out of the mouth, on sleepers.
  for (let i = 0; i < 6; i++) box(g, DWOOD, 0.24, 0.02, 0.04, ax, 0.11, az + 0.02 + i * 0.1);
  for (const k of [-1, 1]) box(g, DSTEEL, 0.018, 0.022, 0.62, ax + k * 0.07, 0.13, az + 0.27);
  // The ore cart on the rails.
  const cart = G(g, ax, 0.14, az + 0.38);
  cart.rotation.y = 0.04;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const w = M(new THREE.CylinderGeometry(0.035, 0.035, 0.02, 10), IRON, cart);
    w.rotation.z = Math.PI / 2;
    w.position.set(sx * 0.08, 0.035, sz * 0.07);
  }
  const bin = M(new THREE.CylinderGeometry(0.15, 0.11, 0.12, 4), WOOD, cart);
  bin.rotation.y = Math.PI / 4;
  bin.scale.set(1, 1, 0.85);
  bin.position.y = 0.12;
  for (const y of [0.08, 0.16]) {
    const b = M(new THREE.CylinderGeometry(0.11 + (y - 0.06) * 0.35, 0.11 + (y - 0.06) * 0.35, 0.015, 4, 1, true), IRON, cart);
    b.rotation.y = Math.PI / 4;
    b.scale.set(1.04, 1, 0.89);
    b.position.y = y;
  }
  for (let i = 0; i < 7; i++) {
    const k = M(new THREE.DodecahedronGeometry(0.035 + rng() * 0.02, 0), i % 3 === 0 ? ORE_GLINT : i % 3 === 1 ? ORE : ORE_RUST, cart);
    k.position.set((rng() - 0.5) * 0.14, 0.19 + rng() * 0.04, (rng() - 0.5) * 0.12);
    k.rotation.set(rng() * 3, rng() * 3, 0);
  }
  // A pick leaning on the props and a spill of ore at the foot.
  const pick = G(g, ax - 0.22, 0.1, az + 0.06);
  pick.rotation.set(0.1, 0.3, 0.32);
  limb(pick, V(0, 0, 0), V(0, 0.32, 0), 0.01, 0.01, WOOD, 4);
  const head = M(new THREE.TorusGeometry(0.07, 0.012, 4, 8, Math.PI * 0.7), DSTEEL, pick);
  head.position.set(0, 0.26, 0);
  head.rotation.z = Math.PI * 0.15;
  for (let i = 0; i < 6; i++) {
    const k = M(new THREE.DodecahedronGeometry(0.03 + rng() * 0.025, 0), i % 2 ? ORE_RUST : ORE_GLINT, g);
    k.position.set(0.26 + (rng() - 0.5) * 0.2, 0.12, 0.22 + (rng() - 0.5) * 0.15);
    k.rotation.set(rng() * 3, rng() * 3, 0);
  }
  return done(c, 1.15);
};

/** What the mine gives up: a heap of rust-dark ore lumps and a few rough iron blooms. */
export const orePile = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 88);
  const { g } = c;
  const rng = mulberry32(881);
  const rows: [number, number][] = [[0, 9], [1, 6], [2, 3], [3, 1]];
  for (const [row, n] of rows) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + row;
      const d = (3 - row) * 0.07;
      const s = 0.06 + rng() * 0.025;
      const k = M(new THREE.DodecahedronGeometry(s, 0), i % 4 === 0 ? ORE_GLINT : i % 2 ? ORE : ORE_RUST, g);
      k.position.set(Math.cos(a) * d, 0.05 + row * 0.08, Math.sin(a) * d);
      k.rotation.set(rng() * 3, rng() * 3, rng() * 3);
    }
  }
  // Rough blooms, hammered flat, leaning on the heap.
  for (const [x, z, ry] of [[0.26, 0.05, 0.4], [-0.2, 0.2, -0.7]] as const) {
    const b = box(g, ORE_GLINT, 0.12, 0.04, 0.07, x, 0.05, z, ry);
    b.rotation.z = 0.35;
  }
  return done(c, 0.4);
};

/* ---------- ruins of the era II buildings ---------- */

/**
 * What a destroyed workshop, clockwork turret or laboratory leaves, in its own material: broken walls or
 * snapped trestle legs, the telling leftover (the cracked flywheel, the turret's head on its side, the
 * still knocked over in a puddle of green) and the rubble around.
 */
export const era2Ruin = (type: string, mat: Era2Material = 2): Model => {
  const c = begin(undefined, 0.7, 91);
  const { g } = c;
  const rng = mulberry32(910 + type.length * 17 + mat * 5);
  const iron = mat === 3;
  const st = mat === 1 ? stone('#77736c') : stone(iron ? IRON_STONE : '#8b857a');
  const scorch = (r: number, x = 0, z = 0, sx = 1): void => {
    const d = M(new THREE.CircleGeometry(r, 12), std('#1e1a16', { roughness: 1, bumpScale: 0 }), g);
    d.rotation.x = -Math.PI / 2;
    d.scale.x = sx;
    d.position.set(x, 0.006, z);
  };
  const walls = (cx: number, cz: number, w: number, d: number): void => {
    for (const [x, z, ww, dd] of [[cx, cz + d / 2, w, 0.06], [cx, cz - d / 2, w, 0.06], [cx - w / 2, cz, 0.06, d], [cx + w / 2, cz, 0.06, d]] as const) {
      const n = Math.max(2, Math.round(Math.max(ww, dd) / 0.18));
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n - 0.5;
        const sh = 0.06 + rng() * 0.2;
        box(g, st, ww > dd ? ww / n - 0.01 : ww, sh, dd > ww ? dd / n - 0.01 : dd, x + (ww > dd ? t * ww : 0), sh / 2, z + (dd > ww ? t * dd : 0));
      }
    }
  };
  const plates = (n: number, r: number): void => {
    if (!iron) return;
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const p = box(g, IRON_PLATE, 0.14 + rng() * 0.08, 0.012, 0.1 + rng() * 0.06, Math.sin(a) * r * (0.6 + rng() * 0.5), 0.02 + rng() * 0.03, Math.cos(a) * r * (0.6 + rng() * 0.5), rng() * 3);
      p.rotation.x = (rng() - 0.5) * 0.6;
    }
    const band = M(new THREE.TorusGeometry(0.22, 0.014, 4, 14, Math.PI * 0.9), IRON_BAND, g);
    band.position.set(r * 0.4, 0.03, -r * 0.5);
    band.rotation.set(-Math.PI / 2 + 0.15, 0, rng() * 3);
  };
  const colors = mat === 1 ? ['#77736c', '#6b675f', '#837e75'] : iron ? ['#72767c', '#5d6167', '#868a90'] : ASHLAR;
  let h = 0.4;
  if (type === 'workshop') {
    scorch(0.45, -0.08, -0.1, 1.3);
    walls(-0.08, -0.1, 0.86, 0.62);
    // The flywheel, cracked and fallen flat; the crane's mast across the rubble.
    const fw = G(g, 0.45, 0.04, 0.25);
    fw.rotation.set(-Math.PI / 2 + 0.2, 0, 0.3);
    M(new THREE.TorusGeometry(0.2, 0.024, 6, 20, Math.PI * 1.6), mat === 1 ? DWOOD : IRON, fw);
    for (let i = 0; i < 4; i++) {
      const a = (i / 6) * Math.PI * 2;
      box(fw, IRON, 0.026, 0.19, 0.02, Math.cos(a) * 0.095, Math.sin(a) * 0.095, 0).rotation.z = a - Math.PI / 2;
    }
    const mast = limb(g, V(-0.5, 0.05, 0.4), V(0.3, 0.1, 0.55), 0.03, 0.03, iron ? IRON : DWOOD, 5);
    mast.position.y += 0.01;
    const gr = G(g, -0.3, 0.03, 0.42);
    gr.rotation.x = -Math.PI / 2;
    gear(gr, 0.085, 10, iron ? PLATE : COPPER, 0.025, IRON);
    splinters(g, 10, 0.5, rng, -0.08, -0.1);
    rubbleAround(g, 14, 0.5, rng, colors, -0.08, -0.1);
  } else if (type === 'clockwork') {
    if (mat === 1) {
      scorch(0.5);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
        const hh = 0.08 + rng() * 0.25;
        limb(g, V(sx * 0.34, 0, sz * 0.34), V(sx * 0.32, hh, sz * 0.32), 0.05, 0.048, rng() < 0.5 ? std('#2b2119', { roughness: 1 }) : WOOD, 6);
        snapTop(g, sx * 0.32, hh, sz * 0.32, 0.045, WOOD, rng);
      }
      splinters(g, 10, 0.4, rng);
    } else {
      const stub = M(new THREE.CylinderGeometry(0.42, 0.45, 0.26, 8), st, g);
      stub.position.y = 0.13;
      stub.rotation.y = Math.PI / 8;
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + rng() * 0.4;
        const sh = 0.05 + rng() * 0.15;
        box(g, st, 0.12, sh, 0.1, Math.sin(a) * 0.36, 0.26 + sh / 2, Math.cos(a) * 0.36, a);
      }
      const core = M(new THREE.CircleGeometry(0.34, 8), stone('#34302b'), g);
      core.position.y = 0.263;
      core.rotation.set(-Math.PI / 2, 0, Math.PI / 8);
      rubbleAround(g, 12, 0.48, rng, colors);
    }
    // The head torn off, lying on its side with the drum and the key, the great gear beside it.
    const hd = G(g, 0.45, 0.1, 0.35);
    hd.rotation.set(0.2, 0.7, 1.35);
    box(hd, PLATE, 0.3, 0.16, 0.3, 0, 0, 0);
    const dm = M(new THREE.CylinderGeometry(0.085, 0.085, 0.15, 10), IRON, hd);
    dm.rotation.x = Math.PI / 2;
    dm.position.y = 0.15;
    for (const k of [-1, 1]) M(new THREE.TorusGeometry(0.045, 0.014, 5, 10), COPPER, hd).position.set(k * 0.05, 0.05, -0.22);
    const gr = G(g, -0.42, 0.03, 0.3);
    gr.rotation.set(-Math.PI / 2, 0, 0.5);
    gear(gr, 0.14, 12, COPPER, 0.035, IRON);
    h = 0.35;
  } else {
    // Laboratory: the walls broken low, the chimney fallen, the still knocked over in a green puddle.
    scorch(0.45, -0.14, -0.14, 1.2);
    walls(-0.14, -0.14, 0.7, 0.62);
    const ch = M(new THREE.CylinderGeometry(0.06, 0.075, 0.6, 8), stone(iron ? '#5d6167' : '#6e6660'), g);
    ch.position.set(-0.1, 0.07, 0.42);
    ch.rotation.set(0, 1.2, Math.PI / 2);
    const pot = M(new THREE.SphereGeometry(0.11, 10, 8), COPPER, g);
    pot.scale.set(1, 0.85, 1);
    pot.position.set(0.42, 0.1, 0.05);
    pot.rotation.z = 1.2;
    const puddle = M(new THREE.CircleGeometry(0.16, 12), brew(BREW.acid, 0.5), g);
    puddle.rotation.x = -Math.PI / 2;
    puddle.scale.y = 0.6;
    puddle.position.set(0.5, 0.01, 0.22);
    glowSprite(g, BREW.acid, 0.3, V(0.5, 0.04, 0.22), 0.45);
    const dome = M(new THREE.SphereGeometry(0.11, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), VERDIGRIS, g);
    dome.position.set(-0.45, 0.06, 0.32);
    dome.rotation.set(1.9, 0, 0.4);
    for (let i = 0; i < 5; i++) {
      const sh = M(new THREE.TetrahedronGeometry(0.02, 0), GLASS, g);
      sh.position.set(0.3 + rng() * 0.3, 0.012, 0.1 + rng() * 0.3);
    }
    rubbleAround(g, 14, 0.5, rng, colors, -0.14, -0.14);
  }
  plates(6, 0.55);
  return done(c, h);
};

/** Ore-flecked crag pieces for the clearing round an iron mine (instanced by the view). */
export const CRAG_MATS = { rock: CRAG, dark: CRAG_DK, ore: ORE_RUST, glint: ORE_GLINT } as const;
